/**
 * @file LeaveTable.jsx
 * @description Enterprise-grade Salesforce Lightning Console Grid Engine featuring dynamic visual formatting.
 * @module modules/leave-management/pages/LeaveTable
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { leaveService } from '../services/leaveService';
import { toast } from 'react-toastify';
import {
    Box, Paper, Typography, Button, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, InputAdornment,
    IconButton, Grid, Chip, Divider, Tabs, Tab
} from '@mui/material';
import {
    Search, ArrowBack, Assignment, Check, Close, Lock, 
    CalendarToday, Person, Description, PlaylistAddCheck, History
} from '@mui/icons-material';

const SLDS_TOKENS = {
    textDark: '#181816',
    textMuted: '#514f4d',
    border: '#dddbda',
    rowHover: '#f3f2f2',
    brandBlue: '#0176d3',
    successGreen: '#2e844a',
    errorRed: '#ea001e',
    warningOrange: '#dd7a01',
    consoleBg: '#f3f2f2',
    successBg: '#edf7ed',
    errorBg: '#fdeded',
    warningBg: '#fff4e5'
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper utility engine to return status-coded dynamic designs
const getStatusChipStyles = (status) => {
    const normalized = String(status || 'Pending').toLowerCase().trim();
    if (normalized === 'approved') {
        return { color: SLDS_TOKENS.successGreen, bgcolor: SLDS_TOKENS.successBg, border: `1px solid ${SLDS_TOKENS.successGreen}` };
    }
    if (normalized === 'rejected' || normalized === 'leave rejected') {
        return { color: SLDS_TOKENS.errorRed, bgcolor: SLDS_TOKENS.errorBg, border: `1px solid ${SLDS_TOKENS.errorRed}` };
    }
    return { color: SLDS_TOKENS.warningOrange, bgcolor: SLDS_TOKENS.warningBg, border: `1px solid ${SLDS_TOKENS.warningOrange}` };
};

// --- SUBSYSTEM: ADVANCED STAGE LIFECYCLE PATH ---
const LightningChevronPath = ({ hrStatus, mgrStatus }) => {
    const sequence = [
        { name: 'System Initialization', checked: true, current: false },
        { name: 'HR Audit Verification', checked: hrStatus === 'Approved', current: hrStatus === 'Pending' },
        { name: 'Manager Final Approvals', checked: mgrStatus === 'Approved', current: hrStatus === 'Approved' && mgrStatus === 'Pending' },
        { name: 'Ledger Registry Settlement', checked: mgrStatus === 'Approved', current: false }
    ];

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflowX: 'auto', py: 1.5, gap: '2px', mb: 3 }}>
            {sequence.map((step, index) => {
                let segmentBg = '#e0e0e0';
                let textCol = SLDS_TOKENS.textMuted;
                if (step.checked) {
                    segmentBg = '#4bca81';
                    textCol = '#ffffff';
                } else if (step.current) {
                    segmentBg = SLDS_TOKENS.brandBlue;
                    textCol = '#ffffff';
                }
                return (
                    <Box key={index} sx={{
                        flex: 1, minWidth: '160px', p: 1.2, textAlign: 'center', fontWeight: 700, fontSize: '11px',
                        backgroundColor: segmentBg, color: textCol, textTransform: 'uppercase',
                        clipPath: 'polygon(0% 0%, 92% 0%, 100% 50%, 92% 100%, 0% 100%, 8% 50%)',
                        transition: 'all 0.25s ease'
                    }}>
                        {step.name}
                    </Box>
                );
            })}
        </Box>
    );
};

// --- SUBSYSTEM: DEEP DRILL-DOWN RECORD INTERFACE ---
const RecordDetailWorkspace = ({ leave, role, onBack, onAction, isMutating }) => {
    const hrStyle = getStatusChipStyles(leave.hr_status);
    const mgrStyle = getStatusChipStyles(leave.manager_status);

    return (
        <Box sx={{ animation: 'fadeIn 0.2s ease-in-out' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onBack} size="small" sx={{ border: `1px solid ${SLDS_TOKENS.border}`, borderRadius: '4px', bgcolor: '#fff' }}>
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: '11px', color: SLDS_TOKENS.textMuted, fontWeight: 700 }}>SALESFORCE OBJECT TRANSACTION RECORD</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: SLDS_TOKENS.textDark, fontSize: '20px' }}>
                            Token Master ID: #{leave.id}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <LightningChevronPath hrStatus={leave.hr_status} mgrStatus={leave.manager_status} />

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper variant="outlined" sx={{ borderColor: SLDS_TOKENS.border, borderRadius: '4px', mb: 3, boxShadow: 'none' }}>
                        <Box sx={{ p: 2, bgcolor: SLDS_TOKENS.consoleBg, borderBottom: `1px solid ${SLDS_TOKENS.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assignment sx={{ color: SLDS_TOKENS.brandBlue, fontSize: 18 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: SLDS_TOKENS.textDark }}>Detailed Technical Field Parameters</Typography>
                        </Box>
                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                {[
                                    { label: 'ALLOCATION CATEGORY', val: leave.leave_type, icon: <Description fontSize="small" /> },
                                    { label: 'AUTHENTICATED USER REFERENCE', val: `UID-${leave.user_id || 'SYSTEM'}`, icon: <Person fontSize="small" /> },
                                    { label: 'SCHEDULED WINDOW OVERTURE', val: formatDate(leave.start_date), icon: <CalendarToday fontSize="small" /> },
                                    { label: 'SCHEDULED WINDOW CLOSURE', val: formatDate(leave.end_date), icon: <CalendarToday fontSize="small" /> }
                                ].map((field, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                        <Stack spacing={0.5} sx={{ p: 1.5, border: `1px solid ${SLDS_TOKENS.border}`, borderRadius: '4px', bgcolor: '#fcfcfc' }}>
                                            <Typography sx={{ fontSize: '10px', color: SLDS_TOKENS.textMuted, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {field.icon} {field.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: SLDS_TOKENS.textDark }}>{field.val || '—'}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        <Typography sx={{ fontSize: '11px', color: SLDS_TOKENS.textMuted, fontWeight: 700 }}>BUSINESS CASE / JUSTIFICATION METADATA STATEMENT</Typography>
                                        <Typography sx={{ p: 2, bgcolor: '#fdfdfd', border: `1px solid ${SLDS_TOKENS.border}`, borderLeft: `4px solid ${SLDS_TOKENS.brandBlue}`, borderRadius: '4px', fontSize: '13px', lineHeight: 1.5 }}>
                                            {leave.reason || 'No written contextual analysis justification records provided by owner profile agent.'}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ borderColor: SLDS_TOKENS.border, borderRadius: '4px', p: 2.5, bgcolor: '#ffffff', boxShadow: 'none' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '13px', mb: 2, color: SLDS_TOKENS.textDark, borderBottom: `1px solid ${SLDS_TOKENS.border}`, pb: 1 }}>
                            Orchestration Sign-Off Panel
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.2, bgcolor: '#fafaf9', border: `1px solid ${SLDS_TOKENS.border}`, borderRadius: '4px' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>HR Gate Node:</Typography>
                                <Chip label={leave.hr_status || 'Pending'} size="small" sx={{ fontWeight: 700, fontSize: '10px', ...hrStyle }} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.2, bgcolor: '#fafaf9', border: `1px solid ${SLDS_TOKENS.border}`, borderRadius: '4px' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Manager Sign-off:</Typography>
                                <Chip label={leave.manager_status || 'Pending'} size="small" sx={{ fontWeight: 700, fontSize: '10px', ...mgrStyle }} />
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            {['HR', 'Manager', 'Admin'].includes(role) ? (
                                <Stack spacing={1}>
                                    <Button fullWidth variant="contained" disabled={isMutating} onClick={() => onAction(leave.id, 'Approved')} startIcon={<Check />} sx={{ bgcolor: SLDS_TOKENS.successGreen, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#226338' }, boxShadow: 'none' }}>
                                        Execute Approval Signature
                                    </Button>
                                    <Button fullWidth variant="outlined" color="error" disabled={isMutating} onClick={() => onAction(leave.id, 'Rejected')} startIcon={<Close />} sx={{ textTransform: 'none', fontWeight: 700, borderColor: SLDS_TOKENS.errorRed, color: SLDS_TOKENS.errorRed }}>
                                        Deny Record Entry
                                    </Button>
                                </Stack>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, border: `1px dashed ${SLDS_TOKENS.border}`, bgcolor: '#f4f6f9', color: SLDS_TOKENS.textMuted }}>
                                    <Lock sx={{ fontSize: 14 }} />
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>ENCRYPTED AUDIT RECORD</Typography>
                                </Box>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

// --- CORE DISPATCH TABLE ---
const LeaveTable = ({ role, initialLeaves = [], initialAttendance = [], onDataMutation }) => {
    const [subTab, setSubTab] = useState(0);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMutating, setIsMutating] = useState(false);

    const filteredRecordsList = useMemo(() => {
        return initialLeaves.filter(rec => 
            String(rec.id).includes(searchQuery) ||
            String(rec.leave_type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(rec.reason || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [initialLeaves, searchQuery]);

    const handleActionExecution = async (id, status) => {
        setIsMutating(true);
        try {
            await leaveService.updateManagerAction({ id, status });
            toast.success(`Pipeline mutation posted status code assignment [${status}] successfully.`);
            setSelectedRecord(null);
            if (onDataMutation) onDataMutation();
        } catch (err) {
            toast.error("Global transactional matrix experienced a write update error.");
        } finally {
            setIsMutating(false);
        }
    };

    if (selectedRecord) {
        return <RecordDetailWorkspace leave={selectedRecord} role={role} onBack={() => setSelectedRecord(null)} onAction={handleActionExecution} isMutating={isMutating} />;
    }

    return (
        <Box>
            <Tabs 
                value={subTab} 
                onChange={(e, v) => setSubTab(v)}
                sx={{
                    mb: 2, borderBottom: `1px solid ${SLDS_TOKENS.border}`,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '12px', color: SLDS_TOKENS.textMuted },
                    '& .Mui-selected': { color: `${SLDS_TOKENS.brandBlue} !important` },
                    '& .MuiTabs-indicator': { bgcolor: SLDS_TOKENS.brandBlue }
                }}
            >
                <Tab icon={<PlaylistAddCheck sx={{ fontSize: 16 }} />} iconPosition="start" label="Structured Absence Ledger Sub-Records" />
                <Tab icon={<History sx={{ fontSize: 16 }} />} iconPosition="start" label="Systems Hardware Architecture Audit Log Stream" />
            </Tabs>

            {subTab === 0 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Filter this view row space..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment>
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { height: '32px', borderRadius: '4px', fontSize: '12px', width: '280px', bgcolor: '#fff' } }}
                        />
                    </Box>

                    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: SLDS_TOKENS.border, borderRadius: '4px', boxShadow: 'none' }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: SLDS_TOKENS.consoleBg }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>OBJECT MASTER REFERENCE</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>CLASSIFICATION POOL</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>START DATEBOUND</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>TERMINATION DATEBOUND</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>HR VERIFICATION NODE</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>MANAGER CONTROL GATE</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRecordsList.map((row) => {
                                    const hrStyles = getStatusChipStyles(row.hr_status);
                                    const mgrStyles = getStatusChipStyles(row.manager_status);
                                    return (
                                        <TableRow key={row.id} onClick={() => setSelectedRecord(row)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: SLDS_TOKENS.rowHover } }}>
                                            <TableCell sx={{ fontWeight: 700, color: SLDS_TOKENS.brandBlue, fontSize: '12px' }}>TRK-{row.id}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>{row.leave_type || 'Unspecified'}</TableCell>
                                            <TableCell sx={{ fontSize: '12px' }}>{formatDate(row.start_date)}</TableCell>
                                            <TableCell sx={{ fontSize: '12px' }}>{formatDate(row.end_date)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={row.hr_status || 'Pending'} sx={{ fontSize: '10px', fontWeight: 700, height: '20px', borderRadius: '3px', ...hrStyles }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={row.manager_status || 'Pending'} sx={{ fontSize: '10px', fontWeight: 700, height: '20px', borderRadius: '3px', ...mgrStyles }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredRecordsList.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: SLDS_TOKENS.textMuted, fontSize: '12px' }}>
                                            No explicit matching records verified in current buffer workspace frame layout.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {subTab === 1 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderColor: SLDS_TOKENS.border, borderRadius: '4px', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: SLDS_TOKENS.consoleBg }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>TIMESTAMP STAMP</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>CLOCK IN EVENT LOG</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>CLOCK OUT EVENT LOG</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>COMPUTED DELTA QUANTUM</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {initialAttendance.map((log, i) => (
                                <TableRow key={i} sx={{ '&:hover': { bgcolor: SLDS_TOKENS.rowHover } }}>
                                    <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>{formatDate(log.date)}</TableCell>
                                    <TableCell sx={{ fontSize: '12px', color: SLDS_TOKENS.successGreen, fontWeight: 600 }}>{log.clock_in || 'System Drop'}</TableCell>
                                    <TableCell sx={{ fontSize: '12px' }}>{log.clock_out || 'Active Execution Terminal'}</TableCell>
                                    <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>{log.total_hours ? `${log.total_hours} Hours` : 'Processing...'}</TableCell>
                                </TableRow>
                            ))}
                            {initialAttendance.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: SLDS_TOKENS.textMuted, fontSize: '12px' }}>
                                        No metrics logging arrays matched in this target temporal execution stream.
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

LeaveTable.propTypes = {
    role: PropTypes.string.isRequired,
    initialLeaves: PropTypes.array,
    initialAttendance: PropTypes.array,
    onDataMutation: PropTypes.func
};

export default LeaveTable;